// Lightweight statistical interpretation layer.
//
// This module turns the percentile sliders already shown on the Player Page
// into a small set of baseball-specific *structural* descriptors across four
// axes: Profile Shape, Signal Consistency, Cohort Position, and Context
// Sensitivity.
//
// Design intent (important): the audience scans the red/blue sliders and forms
// a 2-second verdict ("lots of red = good"). These tags are NOT there to make
// users into analysts. They exist to occasionally *interrupt that certainty* --
// surfacing a nuance ("loud EV but the contact is quiet", "old for the level",
// "this leans on one signal") that makes a user pause and open a deeper page
// (MLB Context / Comps / Splits). Tags flagged `tone: "hook"` are those
// certainty-interrupting moments and carry a suggested deep-dive page.
//
// Nothing here is a hard projection or a quality grade. Every tag describes a
// relationship already present in the data (a percentile split, an age-vs-level
// gap, a divergence between categories).

export type TagDimension = "shape" | "consistency" | "cohort" | "context" | "pitch";
export type TagTone = "neutral" | "hook";
export type TagCta = "context" | "comps" | "splits";

/** One row of the hitter pitch-type performance card. */
export interface PitchRow {
  pitch_type?: string | null;
  xwoba?: number | null;
  xslg?: number | null;
  swings?: number | null;
  takes?: number | null;
  whiffs?: number | null;
  hardhit_percent?: number | null;
  pitch_percent?: number | null;
  batter_run_value_per_100?: number | null;
  velocity?: number | null;
}

export interface ProfileTag {
  /** Extremely short headline label, e.g. "Volatile Power". */
  label: string;
  /** Which of the four interpretation axes this tag answers. */
  dimension: TagDimension;
  /** One-sentence, non-judgmental explanation of the relationship. */
  detail: string;
  /** "hook" = a certainty-interrupting nuance worth exploring. */
  tone: TagTone;
  /** Suggested deeper page to open (only set on hook tags). */
  cta?: TagCta;
}

// ---------------------------------------------------------------------------
// Metric groupings ("composites"). All percentile fields on the backend are
// oriented so higher = further in the good direction (the Savant convention),
// AND they are already computed within the player's level/age/qualifier cohort.
// So each composite is effectively "this skill vs cohort" on a 0-100 scale.
// ---------------------------------------------------------------------------

const HITTER_GROUPS = {
  power: ["ev_p", "ev90_p", "maxev_p", "barrelbbe_p", "hhrate_p", "iso_p", "xslg_p"],
  contact: ["krate_p", "whiffrate_p", "swstr_p", "zcontact_p", "xba_p"],
  discipline: ["bbrate_p", "chaserate_p"],
  outcome: ["xwoba_p", "woba_p", "wrcplus_p", "score_p"],
} as const;

const PITCHER_GROUPS = {
  stuff: ["psStuffAgg_p", "velo_p", "whiffrate_p", "swstr_p", "krate_p", "chaserate_p"],
  command: ["bbrate_p", "strikerate_p", "zonerate_p", "kbb_p"],
  contact: ["hhrate_p", "barrelbbe_p", "ev_p", "gbrate_p"],
  outcome: ["xwoba_p", "woba_p", "fip_p", "score_p"],
} as const;

// Prospect-relevant median age by level (years). A player well under this is
// "young for the level" (a rare/positive amplifier); well over it is "old for
// the level" (an age-dependent-production caution). These are typical MiLB
// prospect ages, deliberately below the raw org-filler averages at the upper
// levels.
const LEVEL_MEDIAN_AGE: Record<string, number> = {
  Rk: 19,
  A: 20,
  "A+": 21,
  AA: 23,
  AAA: 25,
};

// ---------------------------------------------------------------------------
// Numeric helpers
// ---------------------------------------------------------------------------

function toPercentile(raw: unknown): number | null {
  if (raw === null || raw === undefined) return null;
  const n = Number(raw) * 100;
  if (!Number.isFinite(n)) return null;
  if (n < -1 || n > 101) return null;
  // The backend fills non-applicable percentiles with 0 (`df.fillna(0)`), and
  // rank(pct=True) never legitimately yields exactly 0 (the lowest rank is
  // 1/n > 0). So treat an exact 0 as "not populated" -- otherwise placeholder
  // Statcast columns at aggregate levels (AA / A+) drag composites down and
  // fake conflicts on otherwise coherent profiles.
  if (n === 0) return null;
  return Math.min(100, Math.max(0, n));
}

function mean(xs: number[]): number {
  return xs.reduce((a, b) => a + b, 0) / xs.length;
}

function stdDev(xs: number[]): number {
  if (xs.length < 2) return 0;
  const m = mean(xs);
  return Math.sqrt(xs.reduce((a, b) => a + (b - m) * (b - m), 0) / xs.length);
}

function clamp(x: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, x));
}

/** Mean of the available percentiles for a group, or null if none present. */
function composite(
  percentiles: Record<string, unknown>,
  keys: readonly string[]
): number | null {
  const vals: number[] = [];
  for (const k of keys) {
    const v = toPercentile(percentiles[k]);
    if (v !== null) vals.push(v);
  }
  return vals.length ? mean(vals) : null;
}

// ---------------------------------------------------------------------------
// Public entry point
// ---------------------------------------------------------------------------

/**
 * Build the structural interpretation tags for a player's percentile profile.
 *
 * @param percentiles The `_p` percentile object (fields are 0-1 fractions).
 * @param playerData  The raw player row (used for age).
 * @param position    Player position; "P" => pitcher, anything else => hitter.
 * @param level       Level abbreviation ("AAA", "AA", "A+", "A", "Rk").
 * @param pitchProfile Optional hitter pitch-type performance rows.
 */
export function computeProfileTags(
  percentiles: Record<string, unknown> | null | undefined,
  playerData: Record<string, unknown> | null | undefined,
  position: string | null | undefined,
  level: string | null | undefined,
  pitchProfile?: PitchRow[] | null
): ProfileTag[] {
  if (!percentiles) return [];
  const isPitcher = position === "P";
  const groups = isPitcher ? PITCHER_GROUPS : HITTER_GROUPS;

  // Role composites (cohort-relative, 0-100).
  const c: Record<string, number | null> = {};
  for (const key of Object.keys(groups)) {
    c[key] = composite(percentiles, (groups as Record<string, readonly string[]>)[key]);
  }
  const present = Object.values(c).filter((v: number | null): v is number => v !== null);
  if (present.length < 2) return []; // not enough to describe a shape

  // Age relative to level.
  const rawAge = playerData?.age;
  const age = rawAge != null && Number.isFinite(Number(rawAge)) ? Number(rawAge) : null;
  const levelMedian = level ? LEVEL_MEDIAN_AGE[level] : undefined;
  const ageDelta = age != null && levelMedian != null ? age - levelMedian : null;

  // Cross-composite structure used by several axes.
  const compStd = stdDev(present);
  const maxComp = Math.max(...present);
  const minComp = Math.min(...present);
  const maxGap = maxComp - minComp;
  const highs = present.filter((v: number) => v >= 70).length;
  const aboveCount = present.filter((v: number) => v >= 55).length;
  const lows = present.filter((v: number) => v < 45).length;
  const singleSignal = highs >= 1 && aboveCount <= 1;
  // Require at least three populated composites, a genuine top-vs-bottom split,
  // and a wide gap. This keeps coherent all-around profiles (where every
  // populated category is strong) from being mislabeled as conflicting.
  const conflict = present.length >= 3 && maxComp >= 70 && minComp <= 30;

  const tags: ProfileTag[] = [];

  const shape = isPitcher
    ? pitcherShapeTag(c, percentiles, ageDelta)
    : hitterShapeTag(c, ageDelta);
  if (shape) tags.push(shape);

  // Hitter-only: what pitch families they punish vs. get exposed by.
  if (!isPitcher) {
    const pitch = pitchTypeTag(pitchProfile);
    if (pitch) tags.push(pitch);
  }

  const cohort = cohortTag(
    present.length,
    highs,
    aboveCount,
    lows,
    singleSignal,
    maxGap,
    mean(present)
  );
  if (cohort) tags.push(cohort);

  const consistency = consistencyTag(compStd, conflict);
  if (consistency) tags.push(consistency);

  const context = contextTag(compStd, ageDelta, singleSignal, maxGap, conflict);
  if (context) tags.push(context);

  return tags;
}

// ---------------------------------------------------------------------------
// 1. Profile Shape -- baseball-specific rule buckets
// ---------------------------------------------------------------------------

function hitterShapeTag(
  c: Record<string, number | null>,
  ageDelta: number | null
): ProfileTag | null {
  const P = c.power;
  const C = c.contact;
  const D = c.discipline;
  const O = c.outcome;

  // Loud power with a quiet contact rate -- the classic "is this real?" split.
  if (P != null && C != null && P >= 78 && C <= 38) {
    return {
      dimension: "shape",
      tone: "hook",
      cta: "comps",
      label: "Volatile Power",
      detail:
        "Loud exit velocity sitting on top of a below-cohort contact rate. Surface results and swing-and-miss are pulling in different directions.",
    };
  }
  // Strong overall output while notably young for the level -- rare, notable.
  if (O != null && O >= 68 && ageDelta != null && ageDelta <= -1.5) {
    return {
      dimension: "shape",
      tone: "hook",
      cta: "comps",
      label: "Young-for-Level Output",
      detail:
        "Above-cohort production from a player young for the level.",
    };
  }
  if (P != null && C != null && P >= 78 && C >= 58) {
    return {
      dimension: "shape",
      tone: "neutral",
      label: "Power-First Profile",
      detail:
        "Impact metrics lead and the contact rate holds up underneath them. Profile is built around damage on contact.",
    };
  }
  if (C != null && P != null && C >= 70 && P <= 38) {
    return {
      dimension: "shape",
      tone: "neutral",
      label: "Contact-Over-Power",
      detail:
        "Bat-to-ball sits well above the impact metrics. Production leans on making contact rather than driving the ball.",
    };
  }
  if (D != null && D >= 72 && (P == null || P < 60) && (C == null || C < 72)) {
    return {
      dimension: "shape",
      tone: "neutral",
      label: "Discipline-Driven",
      detail:
        "Plate approach (walks / chase) carries the profile more than raw impact or contact skills.",
    };
  }
  // Strong output but old for the level -- production and age disagree.
  if (O != null && O >= 66 && ageDelta != null && ageDelta >= 1.5) {
    return {
      dimension: "shape",
      tone: "hook",
      cta: "context",
      label: "Age-Dependent Output",
      detail:
        "The output is strong, but the player is old for this level — production and age aren't pointing the same way.",
    };
  }
  return balancedOrLeaning(c, ["power", "contact", "discipline"], {
    power: "Power-Leaning",
    contact: "Contact-Leaning",
    discipline: "Discipline-Leaning",
  });
}

function pitcherShapeTag(
  c: Record<string, number | null>,
  percentiles: Record<string, unknown>,
  ageDelta: number | null
): ProfileTag | null {
  const S = c.stuff;
  const Cmd = c.command;
  const CM = c.contact;
  const O = c.outcome;

  // Stuff without the strikes to back it -- reliability question.
  if (S != null && Cmd != null && S >= 78 && Cmd <= 38) {
    return {
      dimension: "shape",
      tone: "hook",
      cta: "comps",
      label: "Stuff-Over-Command",
      detail:
        "Bat-missing stuff paired with a below-cohort command profile.",
    };
  }
  // Velocity that isn't (yet) turning into whiffs.
  const velo = toPercentile(percentiles["velo_p"]);
  const missVals = ["whiffrate_p", "swstr_p", "krate_p"]
    .map((k) => toPercentile(percentiles[k]))
    .filter((v): v is number => v !== null);
  const missMean = missVals.length ? mean(missVals) : null;
  if (velo != null && velo >= 78 && missMean != null && missMean <= 45) {
    return {
      dimension: "shape",
      tone: "hook",
      cta: "context",
      label: "Velocity-Driven Output",
      detail:
        "The velocity ranks near the top of the cohort, but the swing-and-miss hasn't followed it. Radar-gun output and bat-missing are diverging.",
    };
  }
  if (S != null && Cmd != null && S >= 75 && Cmd >= 58) {
    return {
      dimension: "shape",
      tone: "neutral",
      label: "Power-Arsenal Profile",
      detail: "Stuff leads and the command holds up underneath it.",
    };
  }
  if (Cmd != null && (S == null || S < 58) && Cmd >= 72) {
    return {
      dimension: "shape",
      tone: "neutral",
      label: "Command-Driven Profile",
      detail:
        "Strikes and walk avoidance carry the profile more than raw bat-missing stuff.",
    };
  }
  if (CM != null && CM >= 68 && (S == null || S < 58)) {
    return {
      dimension: "shape",
      tone: "neutral",
      label: "Contact-Managed Profile",
      detail:
        "Weak contact (grounders / low hard-hit) does more work here than missing bats outright.",
    };
  }
  if (O != null && O >= 66 && ageDelta != null && ageDelta >= 1.5) {
    return {
      dimension: "shape",
      tone: "hook",
      cta: "context",
      label: "Age-Dependent Output",
      detail:
        "Strong results, but old for the level. Production and age aren't aligned.",
    };
  }
  return balancedOrLeaning(c, ["stuff", "command", "contact"], {
    stuff: "Stuff-Leaning",
    command: "Command-Leaning",
    contact: "Contact-Mgmt Leaning",
  });
}

function balancedOrLeaning(
  c: Record<string, number | null>,
  keys: string[],
  leanLabels: Record<string, string>
): ProfileTag {
  const avail = keys
    .map((k) => ({ k, v: c[k] }))
    .filter((x): x is { k: string; v: number } => x.v != null)
    .sort((a, b) => b.v - a.v);
  if (avail.length >= 2 && avail[0].v - avail[1].v >= 18) {
    return {
      dimension: "shape",
      tone: "neutral",
      label: leanLabels[avail[0].k],
      detail:
        "One category sits clearly above the others, so the profile is shaped around that axis rather than spread evenly.",
    };
  }
  return {
    dimension: "shape",
    tone: "neutral",
    label: "Balanced Profile",
    detail:
      "No single category leads by much — the strengths are distributed fairly evenly across the profile.",
  };
}

// ---------------------------------------------------------------------------
// 2. Signal Consistency -- internal coherence of the composites
// ---------------------------------------------------------------------------

function consistencyTag(compStd: number, conflict: boolean): ProfileTag {
  if (conflict) {
    return {
      dimension: "consistency",
      tone: "hook",
      cta: "comps",
      label: "Conflicting Indicators",
      detail:
        "At least one category ranks near the top of the cohort while another sits near the bottom.",
    };
  }
  if (compStd <= 9) {
    return {
      dimension: "consistency",
      tone: "neutral",
      label: "High Signal Consistency",
      detail:
        "The categories land at similar cohort levels, so the indicators reinforce rather than contradict each other.",
    };
  }
  if (compStd <= 16) {
    return {
      dimension: "consistency",
      tone: "neutral",
      label: "Moderate Signal Divergence",
      detail:
        "The categories mostly agree, with some spread between the strongest and weakest.",
    };
  }
  return {
    dimension: "consistency",
    tone: "neutral",
    label: "High Signal Divergence",
    detail:
      "The categories are spread widely — the profile is uneven rather than uniform across skills.",
  };
}

// ---------------------------------------------------------------------------
// 3. Cohort Relative Position -- how the profile sits vs the level/age baseline
// ---------------------------------------------------------------------------

function cohortTag(
  n: number,
  highs: number,
  aboveCount: number,
  lows: number,
  singleSignal: boolean,
  maxGap: number,
  meanComp: number
): ProfileTag {
  if (aboveCount >= 3) {
    return {
      dimension: "cohort",
      tone: "neutral",
      label: "Above Cohort (Multi-Signal)",
      detail:
        "Multiple categories clear the cohort baseline. The profile is above average on more than one front.",
    };
  }
  if (singleSignal) {
    return {
      dimension: "cohort",
      tone: "hook",
      cta: "comps",
      label: "Above Cohort (Single-Signal)",
      detail:
        "The profile clears the cohort baseline, but almost entirely on one category.",
    };
  }
  if (lows >= Math.max(2, Math.ceil(n * 0.6))) {
    return {
      dimension: "cohort",
      tone: "neutral",
      label: "Below Cohort (Select Signals)",
      detail:
        "Most categories sit below the cohort baseline, with strength concentrated in only a few areas.",
    };
  }
  if (maxGap <= 14 && meanComp >= 44 && meanComp <= 58) {
    return {
      dimension: "cohort",
      tone: "neutral",
      label: "Near Cohort Median",
      detail:
        "The categories cluster around the middle of the cohort — a fairly typical profile for this level and age band.",
    };
  }
  return {
    dimension: "cohort",
    tone: "neutral",
    label: "Mixed Cohort Positioning",
    detail:
      "The profile lands above the cohort in some categories and below it in others, without a single clear tilt.",
  };
}

// ---------------------------------------------------------------------------
// 4. Context Sensitivity -- how environment-dependent the signal looks
// ---------------------------------------------------------------------------

function contextTag(
  compStd: number,
  ageDelta: number | null,
  singleSignal: boolean,
  maxGap: number,
  conflict: boolean
): ProfileTag {
  let sens = 0;
  sens += clamp(compStd - 8, 0, 30); // divergence => more skill-specific
  if (ageDelta != null) {
    if (ageDelta >= 3) sens += 28;
    else if (ageDelta >= 1.5) sens += 16;
    else if (ageDelta <= -1.5) sens -= 8; // young for level = steadier context
  }
  if (singleSignal) sens += 16;
  if (maxGap >= 35) sens += 14;
  if (conflict) sens += 12;

  if (sens < 16) {
    return {
      dimension: "context",
      tone: "neutral",
      label: "Low Context Sensitivity",
      detail:
        "The categories move together and the age fits the level, so the profile reads consistently rather than hinging on a specific environment.",
    };
  }
  if (sens < 32) {
    return {
      dimension: "context",
      tone: "neutral",
      label: "Moderate Context Sensitivity",
      detail:
        "Some of the profile leans on specific skills or level/age factors — a few pieces are more environment-dependent than others.",
    };
  }
  if (sens < 50) {
    return {
      dimension: "context",
      tone: "hook",
      cta: "context",
      label: "High Context Sensitivity",
      detail:
        "A lot of this profile depends on level and age context.",
    };
  }
  return {
    dimension: "context",
    tone: "hook",
    cta: "context",
    label: "Context-Dependent Profile",
    detail:
      "The output leans heavily on level, age, and a narrow set of signals.",
  };
}

// ---------------------------------------------------------------------------
// 5. Pitch-Type Read (hitters) -- which pitch families a hitter punishes vs.
// gets exposed by.
//
// Reliability hierarchy (this drives the logic order): performance *by pitch
// type* is one of the noisiest cuts in baseball. Pitch-value / xwOBA-by-pitch
// has a year-to-year correlation below ~0.25 and is very sample-sensitive -- a
// handful of balls in play, or a single misclassified pitch, can swing a family
// average. Contact skill (whiff rate) is a K%-type signal that stabilizes far
// faster. So we treat WHIFF as the primary "is there a hole here" trigger and
// DAMAGE (xwOBA + xSLG + HardHit%, blended so no single near-zero pitch type
// dominates) only as confirmation, always measured against the hitter's own
// baseline. A vulnerability tag needs BOTH signals to agree.
//
// Because the underlying signal is noisy and descriptive (not predictive),
// these tags are deliberately framed as low-confidence "worth a look" hooks,
// never verdicts -- which is exactly their job: interrupt certainty and push
// toward the deeper MLB-context page.
// ---------------------------------------------------------------------------

type PitchFamily = "fastball" | "breaking" | "offspeed";

const PITCH_FAMILY: Record<string, PitchFamily> = {
  FF: "fastball", FA: "fastball", SI: "fastball", FT: "fastball", FC: "fastball",
  SL: "breaking", ST: "breaking", SV: "breaking", SC: "breaking",
  CU: "breaking", KC: "breaking", CS: "breaking",
  CH: "offspeed", FS: "offspeed", FO: "offspeed",
};

// Sample floors. 25 pitches (~5-8 balls in play) is far too few for any damage
// read; pitch-type xwOBA needs a lot more before it means anything. So a family
// must clear a real pitch floor to be "trusted" at all, and must clear a swings
// floor before we'll draw a whiff-based conclusion from it.
const MIN_FAMILY_PITCHES = 40;
const MIN_FAMILY_SWINGS = 20;

function num(x: unknown): number | null {
  if (x === null || x === undefined) return null;
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
}

interface FamilyStats {
  ok: boolean;
  swingsOk: boolean; // enough swings to trust a whiff-based read
  xwoba: number; // pitch-weighted xwOBA
  xslg: number; // pitch-weighted xSLG
  hardhit: number; // pitch-weighted HardHit%
  whiff: number; // whiffs / swings * 100
  pitches: number;
  swings: number;
}

function emptyFamily() {
  return {
    xwNum: 0, xwDen: 0,
    slgNum: 0, slgDen: 0,
    hhNum: 0, hhDen: 0,
    swings: 0,
    whiffs: 0,
    pitches: 0,
  };
}

function pitchTypeTag(rows: PitchRow[] | null | undefined): ProfileTag | null {
  if (!rows || rows.length === 0) return null;

  const raw: Record<PitchFamily, ReturnType<typeof emptyFamily>> = {
    fastball: emptyFamily(),
    breaking: emptyFamily(),
    offspeed: emptyFamily(),
  };

  for (const r of rows) {
    const code = r.pitch_type ? String(r.pitch_type).toUpperCase() : "";
    const fam = PITCH_FAMILY[code];
    if (!fam) continue;
    const swings = num(r.swings) ?? 0;
    const takes = num(r.takes) ?? 0;
    const pitches = swings + takes;
    if (pitches <= 0) continue;
    const a = raw[fam];
    a.pitches += pitches;
    a.swings += swings;
    a.whiffs += num(r.whiffs) ?? 0;
    // Damage metrics are pitch-weighted so families combine cleanly; blending
    // xwOBA + xSLG + HardHit% is far more robust than xwOBA alone against a
    // single small-sample pitch type sitting at a near-zero value.
    const xw = num(r.xwoba);
    if (xw != null) { a.xwNum += xw * pitches; a.xwDen += pitches; }
    const xs = num(r.xslg);
    if (xs != null) { a.slgNum += xs * pitches; a.slgDen += pitches; }
    const hh = num(r.hardhit_percent);
    if (hh != null) { a.hhNum += hh * pitches; a.hhDen += pitches; }
  }

  const stats = (f: PitchFamily): FamilyStats => {
    const a = raw[f];
    return {
      ok: a.pitches >= MIN_FAMILY_PITCHES && a.xwDen > 0,
      swingsOk: a.swings >= MIN_FAMILY_SWINGS,
      xwoba: a.xwDen > 0 ? a.xwNum / a.xwDen : NaN,
      xslg: a.slgDen > 0 ? a.slgNum / a.slgDen : NaN,
      hardhit: a.hhDen > 0 ? a.hhNum / a.hhDen : NaN,
      whiff: a.swings > 0 ? (a.whiffs / a.swings) * 100 : NaN,
      pitches: a.pitches,
      swings: a.swings,
    };
  };

  const fb = stats("fastball");
  const brk = stats("breaking");
  const off = stats("offspeed");
  const families = [fb, brk, off].filter((s) => s.ok);
  if (families.length < 2) return null; // need a real comparison

  // Pitch-weighted baselines across trusted families (the hitter's own norm).
  const wmean = (get: (f: FamilyStats) => number): number => {
    let n = 0;
    let d = 0;
    for (const f of families) {
      const v = get(f);
      if (Number.isFinite(v)) {
        n += v * f.pitches;
        d += f.pitches;
      }
    }
    return d > 0 ? n / d : NaN;
  };
  const baseXwoba = wmean((f) => f.xwoba);
  const baseXslg = wmean((f) => f.xslg);
  const baseHardhit = wmean((f) => f.hardhit);

  // Combined non-fastball damage + contact (breaking + offspeed).
  const nonFamilies = [brk, off].filter((s) => s.ok);
  const nonFbPitches = nonFamilies.reduce((s, f) => s + f.pitches, 0);
  const nonFbXwoba =
    nonFbPitches > 0
      ? nonFamilies.reduce((s, f) => s + f.xwoba * f.pitches, 0) / nonFbPitches
      : NaN;
  const nonFbSwings = nonFamilies.reduce((s, f) => s + f.swings, 0);
  const nonFbWhiffs = nonFamilies.reduce(
    (s, f) => s + (Number.isFinite(f.whiff) ? (f.whiff / 100) * f.swings : 0),
    0
  );
  const nonFbWhiff = nonFbSwings > 0 ? (nonFbWhiffs / nonFbSwings) * 100 : NaN;
  const nonFbOk = nonFamilies.length > 0 && Number.isFinite(nonFbXwoba);

  // A family's results count as "soft" only when xwOBA is below the hitter's
  // own baseline AND at least one of xSLG / HardHit% agrees. This keeps a lone
  // noisy xwOBA (e.g. a near-zero small-sample pitch type) from faking a hole,
  // and is the *confirmation* half of every vulnerability tag.
  const damageSoft = (f: FamilyStats): boolean => {
    if (!Number.isFinite(f.xwoba) || !Number.isFinite(baseXwoba)) return false;
    if (f.xwoba > baseXwoba) return false;
    const hasSlg = Number.isFinite(f.xslg) && Number.isFinite(baseXslg);
    const hasHh = Number.isFinite(f.hardhit) && Number.isFinite(baseHardhit);
    if (!hasSlg && !hasHh) return true; // xwOBA alone if nothing corroborates
    return (hasSlg && f.xslg <= baseXslg) || (hasHh && f.hardhit <= baseHardhit);
  };

  // 1) Fastball-Dependent -- damage concentrated on fastballs, confirmed by more
  // swing-and-miss on everything else. Whiff (the reliable contact signal) is the
  // confirmation; the xwOBA gap is the trigger. Minor-league fastballs are more
  // hittable than what's coming, so this is the marquee translation hook. When
  // whiff data is missing we fall back to the production gap alone.
  const nonFbWhiffElevated =
    Number.isFinite(fb.whiff) && Number.isFinite(nonFbWhiff)
      ? nonFbWhiff - fb.whiff >= 5
      : true;
  if (
    fb.ok &&
    nonFbOk &&
    fb.xwoba - nonFbXwoba >= 0.055 &&
    fb.xwoba >= 0.34 &&
    nonFbWhiffElevated
  ) {
    return {
      dimension: "pitch",
      tone: "hook",
      cta: "context",
      label: "Fastball-Dependent",
      detail:
        "The damage is concentrated against fastballs, with softer results and more swing-and-miss on spin and offspeed. Minor-league fastballs are more hittable than big-league ones.",
    };
  }

  // 2) Spin-Vulnerable -- a hole against breaking balls. Contact is the primary
  // signal (whiffing spin clearly more than fastballs); damage must be soft too,
  // so a hitter who whiffs on spin but crushes it when he connects is NOT flagged.
  // Both signals must agree -- we do not fire on a noisy xwOBA gap alone.
  const brkWhiffElevated =
    fb.ok &&
    Number.isFinite(brk.whiff) &&
    brk.whiff >= 33 &&
    brk.whiff - fb.whiff >= 8;
  if (brk.ok && brk.swingsOk && brkWhiffElevated && damageSoft(brk)) {
    return {
      dimension: "pitch",
      tone: "hook",
      cta: "context",
      label: "Spin-Vulnerable",
      detail:
        "Breaking balls draw both more swing-and-miss and softer contact than fastballs, a pitch-type hole sharper spin at higher levels tends to expose.",
    };
  }

  // 3) Offspeed-Vulnerable -- exposed by changeups / splits. Same contact-led,
  // damage-confirmed logic as spin.
  const offWhiffElevated =
    fb.ok &&
    Number.isFinite(off.whiff) &&
    off.whiff >= 33 &&
    off.whiff - fb.whiff >= 8;
  if (off.ok && off.swingsOk && offWhiffElevated && damageSoft(off)) {
    return {
      dimension: "pitch",
      tone: "hook",
      cta: "context",
      label: "Offspeed-Vulnerable",
      detail:
        "Changeups and splits draw more swing-and-miss and softer contact than the rest of the profile — a chase / soft-contact hole that better offspeed can widen.",
    };
  }

  // 4) Handles Spin -- reassuring: no fastball-only illusion.
  if (brk.ok && fb.ok && brk.xwoba >= fb.xwoba && brk.xwoba >= 0.33 && !damageSoft(brk)) {
    return {
      dimension: "pitch",
      tone: "neutral",
      label: "Handles Spin",
      detail:
        "Production against breaking balls holds up as well as against fastballs.",
    };
  }

  // 5) Even vs Pitch Types -- balanced across families.
  const xwobas = families.map((f) => f.xwoba);
  if (families.length >= 3 && Math.max(...xwobas) - Math.min(...xwobas) <= 0.045) {
    return {
      dimension: "pitch",
      tone: "neutral",
      label: "Even vs Pitch Types",
      detail:
        "xwOBA is similar across fastballs, breaking, and offspeed. The profile doesn't lean on beating up one pitch group.",
    };
  }

  return null;
}
